/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Drawer } from 'antd';
import { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { TELEMETRY_BETA_CONFIG } from '@/config/embeddedApps';
import { useEmbeddedApp, EmbeddedDrawerState } from '@/hooks/useEmbeddedApp';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

type AuthState = 'loading' | 'authenticated' | 'error';

function getTelemetryBetaEnv(env: string): string {
  // Telemetry Beta uses 'localhost' for local development, not 'local'
  if (env === 'local') {
    return 'localhost';
  }
  return env;
}

function getMimicApiUrl(): string {
  return window.mimicConfig.MIMIC_MGMT_PLANE_API as string;
}

const DEFAULT_IFRAME_STYLE: CSSProperties = {
  width: '100%',
  flex: 1,
  border: 'none',
};

const FULLSCREEN_CONTAINER_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 9999,
  backgroundColor: '#fff',
  display: 'flex',
};

const FULLSCREEN_IFRAME_STYLE: CSSProperties = {
  width: '100%',
  height: '100%',
  border: 'none',
};

export type EmbeddedTelemetryViewerProps = {
  adminMode?: boolean;
  nodeId?: string;
  path?: string;
  iframeStyle?: CSSProperties;
  iframeTitle?: string;
};

export function EmbeddedTelemetryViewer({
  adminMode = false,
  nodeId,
  path,
  iframeStyle = DEFAULT_IFRAME_STYLE,
  iframeTitle = 'Telemetry',
}: EmbeddedTelemetryViewerProps) {
  const { t } = useMimicTranslation('global');
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const selectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedSubtenantID = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const additionalParams = useMemo(() => {
    const params: Record<string, string> = { adminMode: String(adminMode) };

    if (path) {
      params.path = path;
    }

    if (selectedTenantID) {
      params.tenant = selectedTenantID;
    }

    if (selectedSubtenantID) {
      params.subtenantId = selectedSubtenantID;
    }

    if (nodeId) {
      params.nodeIds = nodeId;
      params.embedMode = 'node';
    }

    return params;
  }, [adminMode, path, selectedTenantID, selectedSubtenantID, nodeId]);

  const { iframeRef, iframeSrc, env, fullscreen, drawer, closeDrawer } = useEmbeddedApp(
    TELEMETRY_BETA_CONFIG,
    additionalParams,
  );

  const { telemetryBetaBaseUrl, telemetryBetaOrigin } = useMemo(() => {
    if (!iframeSrc) {
      return { telemetryBetaBaseUrl: '', telemetryBetaOrigin: '' };
    }
    const parsed = new URL(iframeSrc);
    parsed.search = '';
    parsed.hash = '';
    return { telemetryBetaBaseUrl: parsed.toString(), telemetryBetaOrigin: parsed.origin };
  }, [iframeSrc]);
  const allowedOrigins = useMemo(() => TELEMETRY_BETA_CONFIG.getAllowedOrigins(env), [env]);
  const drawerIframeSrc = buildDrawerIframeSrc(telemetryBetaBaseUrl, additionalParams, drawer);

  useEffect(() => {
    async function authenticate() {
      try {
        const { data: tokenInfo } = await mgmtPlaneAPIClient.auth.generateToken({ maxAge: 15 });

        const telemetryBetaEnv = getTelemetryBetaEnv(env);
        const body: Record<string, string> = {
          sessionId: tokenInfo.token,
          env: telemetryBetaEnv,
        };

        if (telemetryBetaEnv === 'localhost') {
          body.apiUrl = getMimicApiUrl();
        }

        const response = await fetch(`${telemetryBetaOrigin}/api/auth/sso`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          setAuthState('authenticated');
        } else {
          const data = await response.json().catch(() => ({}));
          setErrorMessage(data.message || `Authentication failed (${response.status})`);
          setAuthState('error');
        }
      } catch {
        setErrorMessage('Failed to authenticate');
        setAuthState('error');
      }
    }

    authenticate();
  }, [env, telemetryBetaOrigin]);

  let content: ReactNode;

  if (authState === 'loading') {
    content = (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: '200px' }}>
        {t('loading')}...
      </div>
    );
  } else if (authState === 'error') {
    content = (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          minHeight: '200px',
          color: 'red',
        }}
      >
        {errorMessage}
      </div>
    );
  } else {
    content = (
      <iframe
        ref={iframeRef}
        key={`${selectedTenantID}-${selectedSubtenantID ?? 'tenant'}-${nodeId ?? 'all'}`}
        src={iframeSrc}
        allow="clipboard-read; clipboard-write; fullscreen; downloads"
        style={fullscreen ? FULLSCREEN_IFRAME_STYLE : iframeStyle}
        title={iframeTitle}
      />
    );
  }

  const wrapped = fullscreen ? <div style={FULLSCREEN_CONTAINER_STYLE}>{content}</div> : content;

  return (
    <>
      {wrapped}
      <DetailDrawerOverlay
        open={drawer.open}
        src={drawerIframeSrc}
        allowedOrigins={allowedOrigins}
        onClose={closeDrawer}
      />
    </>
  );
}

function buildDrawerIframeSrc(
  baseUrl: string,
  additionalParams: Record<string, string>,
  drawer: EmbeddedDrawerState,
): string {
  if (!baseUrl || !drawer.open || !drawer.eventId || !drawer.eventTs) return '';

  const url = new URL(baseUrl);

  Object.entries(additionalParams).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  url.searchParams.set('drawerOnly', 'true');
  url.searchParams.set('eventId', drawer.eventId);
  url.searchParams.set('eventTs', drawer.eventTs);

  return url.toString();
}

function useDrawerIframeClose(
  open: boolean,
  allowedOrigins: string[],
  onClose: () => void,
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
) {
  useEffect(() => {
    if (!open) return undefined;
    function handler(event: MessageEvent) {
      if (!allowedOrigins.includes(event.origin)) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (typeof event.data === 'object' && event.data?.type === 'DRAWER_CLOSE') onClose();
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [open, allowedOrigins, onClose, iframeRef]);
}

function DetailDrawerOverlay({
  open,
  src,
  allowedOrigins,
  onClose,
}: {
  open: boolean;
  src: string;
  allowedOrigins: string[];
  onClose: () => void;
}) {
  const drawerIframeRef = useRef<HTMLIFrameElement>(null);
  useDrawerIframeClose(open, allowedOrigins, onClose, drawerIframeRef);
  return (
    <Drawer
      placement="right"
      open={open}
      width="40%"
      onClose={onClose}
      closable={false}
      styles={{ header: { display: 'none' }, body: { padding: 0 } }}
      destroyOnClose
    >
      {open && src && (
        <iframe
          ref={drawerIframeRef}
          src={src}
          allow="clipboard-read; clipboard-write"
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="Event details"
        />
      )}
    </Drawer>
  );
}
